import { isString } from 'remeda'
import aliases from '../../icon-aliases.json'

// Public types ----------------------------------------------------------------

export type IconWeight =
  | 'thin'
  | 'light'
  | 'regular'
  | 'bold'
  | 'fill'
  | 'duotone'

export type IconSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

export type IconSource =
  | { readonly status: 'empty' }
  | { readonly status: 'invalid' }
  | {
      readonly status: 'resolved'
      readonly name: string
      readonly weight: IconWeight
      readonly path: string
    }

// Constants -------------------------------------------------------------------

const ICON_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const WEIGHT_SUFFIX_PATTERN = /-(thin|light|bold|fill|duotone)$/
const ICON_ALIASES: ReadonlyMap<string, string> = new Map(
  Object.entries(aliases)
)

const SYSTEM_ICON_ALIASES: ReadonlyMap<string, string> = new Map([
  ['angles-left', 'caret-double-left'],
  ['angles-right', 'caret-double-right'],
  ['backward', 'rewind'],
  ['backward-step', 'skip-back'],
  ['bars', 'list'],
  ['chevron-down', 'caret-down'],
  ['chevron-left', 'caret-left'],
  ['chevron-right', 'caret-right'],
  ['closed-captioning-slash', 'subtitles-slash'],
  ['compress', 'corners-in'],
  ['ellipsis', 'dots-three'],
  ['ellipsis-vertical', 'dots-three-vertical'],
  ['expand', 'corners-out'],
  ['filter', 'funnel'],
  ['forward', 'fast-forward'],
  ['file-excel', 'file-xls'],
  ['file-powerpoint', 'file-ppt'],
  ['file-word', 'file-doc'],
  ['file-zipper', 'file-zip'],
  ['forward-step', 'skip-forward'],
  ['grip-vertical', 'dots-six-vertical'],
  ['indeterminate', 'minus'],
  ['table-columns', 'columns'],
  ['thumbtack', 'push-pin'],
  ['up-down', 'arrows-down-up'],
  ['volume', 'speaker-high'],
  ['volume-low', 'speaker-low'],
  ['volume-xmark', 'speaker-x'],
  ['xmark', 'x'],
  ['circle-question', 'question'],
  ['circle-xmark', 'x-circle']
])

// Private functions -----------------------------------------------------------

// Reads a recognized suffix without treating arbitrary text as a weight.
const suffixWeight = (name: string): IconWeight => {
  const suffix = name.match(WEIGHT_SUFFIX_PATTERN)?.[1]

  return suffix === 'thin' ||
    suffix === 'light' ||
    suffix === 'bold' ||
    suffix === 'fill' ||
    suffix === 'duotone'
    ? suffix
    : 'regular'
}

// Applies an explicit nonregular weight after removing an encoded suffix.
const resolveValidIcon = (name: string, weight: IconWeight): IconSource => {
  const mappedName = ICON_ALIASES.get(name) ?? name
  const resolvedWeight =
    weight === 'regular' ? suffixWeight(mappedName) : weight
  const baseName = mappedName.replace(WEIGHT_SUFFIX_PATTERN, '')
  const resolvedName =
    resolvedWeight === 'regular' ? baseName : `${baseName}-${resolvedWeight}`

  return {
    status: 'resolved',
    name: resolvedName,
    weight: resolvedWeight,
    path: `${resolvedWeight}/${resolvedName}.svg`
  }
}

// Public functions ------------------------------------------------------------

// Accepts safe custom names and distinguishes absence from malformed input.
export const resolveIcon = (
  name: unknown,
  weight: IconWeight = 'regular'
): IconSource => {
  const absent = name === '' || name === null || name === undefined
  const unresolved: IconSource = { status: absent ? 'empty' : 'invalid' }

  return isString(name) && ICON_NAME_PATTERN.test(name)
    ? resolveValidIcon(name, weight)
    : unresolved
}

// Preserves Web Awesome's outlined and filled rating states.
export const resolveSystemIcon = (
  name: string,
  variant = 'solid'
): IconSource =>
  resolveIcon(
    SYSTEM_ICON_ALIASES.get(name) ?? name,
    name === 'star' && variant === 'solid' ? 'fill' : 'regular'
  )
